/**
 * Ribbon upload: RibbonMeshBuffers (from @nanome-lite/ribbon, or the built-in fallback) -> indexed mesh with a lit
 * vertex-colour shader (URP Lit: energy-conserving Lambert, flat ambient, GGX specular at smoothness 0.15), back-face
 * culled (RibbonMaterial.mat Cull Back). residueIndex per vertex drives picking (kind 'ribbon') and the inverse-hull highlight.
 */
import { BackSide, BufferAttribute, BufferGeometry, DoubleSide, FrontSide, LessEqualDepth, Mesh, RawShaderMaterial, Vector3 } from 'three';
import type { RibbonMeshBuffers } from '@nanome-lite/ribbon';
import { INTERACTION } from '@nanome-lite/look';
import { RIBBON_BRDF, SURFACE_BRDF, brdfUniforms, makeMaterial, type SharedUniforms } from './gl';
import type { UrpBrdf } from './lighting';
import { RENDER_ORDER } from './impostors';
import { encodePickId, pickIdToBytes } from './pick-id';
import { MESH_FRAG, MESH_VERT } from './shaders';

/** Materials for lit meshes (ribbons and surfaces) and their pick / hull / wireframe variants. */
export class MeshMaterials {
  readonly ribbonLit: RawShaderMaterial;
  readonly surfaceLitOpaque: RawShaderMaterial;
  readonly surfaceLitBack: RawShaderMaterial;
  readonly surfaceLitFront: RawShaderMaterial;
  readonly surfaceWire: RawShaderMaterial;
  readonly pick: RawShaderMaterial;
  readonly pickPushed: RawShaderMaterial;
  /** shadow casters: ribbons Cull Back (URP Lit ShadowCaster), surfaces two-sided (SurfaceRenderer.prefab m_CastShadows 2) */
  readonly ribbonShadow: RawShaderMaterial;
  readonly surfaceShadow: RawShaderMaterial;
  private readonly hulls = new Map<string, RawShaderMaterial>();
  private readonly all: RawShaderMaterial[] = [];

  constructor(private readonly shared: SharedUniforms) {
    this.extra = (brdf: UrpBrdf) => ({
      ...brdfUniforms(brdf),
      uOpacity: { value: 1 }, uShellColor: { value: new Vector3() }, uHullSize: { value: 0 }, uPickDepthBias: { value: 0 },
    });
    const extra = this.extra;
    const reg = (m: RawShaderMaterial) => { m.depthFunc = LessEqualDepth; this.all.push(m); return m; };
    this.ribbonLit = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(RIBBON_BRDF), side: FrontSide, blend: false }));
    this.surfaceLitOpaque = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(SURFACE_BRDF), side: DoubleSide, blend: false }));
    // Transparent surfaces: back faces then front faces, blended, depth written (SurfaceRenderer.cs:93-112).
    this.surfaceLitBack = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(SURFACE_BRDF), side: BackSide, blend: true, depthWrite: true }));
    this.surfaceLitFront = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(SURFACE_BRDF), side: FrontSide, blend: true, depthWrite: true }));
    this.surfaceWire = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(SURFACE_BRDF), defines: { UNLIT: 1 }, side: DoubleSide, blend: false }));
    this.pick = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(RIBBON_BRDF), defines: { PICK: 1 }, side: DoubleSide, blend: false }));
    // Ribbon ids sit a hair behind atoms so an atom over a ribbon wins the pick (ToolsController.cs:100-105).
    this.pickPushed = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: { ...extra(RIBBON_BRDF), uPickDepthBias: { value: 2e-4 } }, defines: { PICK: 1 }, side: DoubleSide, blend: false }));
    const shadow = (side: typeof FrontSide | typeof DoubleSide) => {
      const m = reg(makeMaterial(MESH_VERT, MESH_FRAG, shared, { uniforms: extra(RIBBON_BRDF), defines: { SHADOW: 1 }, side, blend: false }));
      m.colorWrite = false;
      return m;
    };
    this.ribbonShadow = shadow(FrontSide);
    this.surfaceShadow = shadow(DoubleSide);
  }

  private readonly extra: (brdf: UrpBrdf) => Record<string, { value: unknown }>;

  /**
   * Back/front materials for one transparent surface. Built from the shared uniforms (not cloned): Material.clone()
   * deep-copies uniform values, which would freeze the shadow matrix and drop the shadow map texture.
   */
  transparentPair(opacity: number): { back: RawShaderMaterial; front: RawShaderMaterial } {
    const mk = (side: typeof BackSide | typeof FrontSide) => {
      const m = makeMaterial(MESH_VERT, MESH_FRAG, this.shared, { uniforms: { ...this.extra(SURFACE_BRDF), uOpacity: { value: opacity } }, side, blend: true, depthWrite: true });
      m.depthFunc = LessEqualDepth;
      return m;
    };
    return { back: mk(BackSide), front: mk(FrontSide) };
  }

  /** Inverse hull: vertices pushed out along the normal by INTERACTION.ribbonInverseHullSize Å, front faces culled. */
  hull(colorHex: number): RawShaderMaterial {
    const key = colorHex.toString(16);
    let m = this.hulls.get(key);
    if (m) return m;
    const c = new Vector3(((colorHex >> 16) & 255) / 255, ((colorHex >> 8) & 255) / 255, (colorHex & 255) / 255);
    m = makeMaterial(MESH_VERT, MESH_FRAG, this.shared, {
      defines: { HULL: 1 }, side: BackSide, blend: false,
      uniforms: { uShellColor: { value: c }, uHullSize: { value: INTERACTION.ribbonInverseHullSize }, uOpacity: { value: 1 }, uPickDepthBias: { value: 0 } },
    });
    m.depthFunc = LessEqualDepth;
    this.hulls.set(key, m);
    this.all.push(m);
    return m;
  }

  dispose(): void { for (const m of this.all) m.dispose(); this.hulls.clear(); }
}

function meshOf(geometry: BufferGeometry, material: RawShaderMaterial, renderOrder: number): Mesh {
  const m = new Mesh(geometry, material);
  m.frustumCulled = false;
  m.matrixAutoUpdate = false;
  m.renderOrder = renderOrder;
  return m;
}

export type HullLayer = 'hover' | 'selection';

/** Average the normals of coincident vertices so an inverse hull inflates as one closed shell. */
export function averagedNormals(position: Float32Array, normal: Float32Array): Float32Array {
  const n = position.length / 3;
  const groups = new Map<string, number[]>();
  for (let i = 0; i < n; i++) {
    const k = `${position[i * 3]!.toFixed(4)},${position[i * 3 + 1]!.toFixed(4)},${position[i * 3 + 2]!.toFixed(4)}`;
    const g = groups.get(k);
    if (g) g.push(i); else groups.set(k, [i]);
  }
  const out = new Float32Array(n * 3);
  for (const g of groups.values()) {
    let x = 0, y = 0, z = 0;
    for (const i of g) { x += normal[i * 3]!; y += normal[i * 3 + 1]!; z += normal[i * 3 + 2]!; }
    const l = Math.hypot(x, y, z) || 1;
    for (const i of g) { out[i * 3] = x / l; out[i * 3 + 1] = y / l; out[i * 3 + 2] = z / l; }
  }
  return out;
}

export class RibbonMesh {
  readonly geometry: BufferGeometry;
  readonly residueIndex: Uint32Array;
  readonly main: Mesh;
  readonly pick: Mesh;
  /** shadow caster (same geometry, light-space pass) */
  readonly shadow: Mesh;
  /** inverse hull meshes, one per layer (hover colour, selection white) */
  readonly hulls: Record<HullLayer, Mesh>;
  private readonly hullGeometry: Record<HullLayer, BufferGeometry>;
  private readonly highlight: Record<HullLayer, BufferAttribute>;
  private readonly current: Record<HullLayer, Set<number> | null> = { hover: null, selection: null };

  constructor(buffers: RibbonMeshBuffers, materials: MeshMaterials, pickSlot: number) {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(buffers.position, 3));
    g.setAttribute('normal', new BufferAttribute(buffers.normal, 3));
    g.setAttribute('color', new BufferAttribute(buffers.color, 3));
    const pick = new Uint8Array(buffers.vertexCount * 4);
    for (let i = 0; i < buffers.vertexCount; i++) pickIdToBytes(encodePickId(pickSlot, buffers.residueIndex[i]!), pick, i * 4);
    g.setAttribute('aPick', new BufferAttribute(pick, 4, true));
    g.setAttribute('aHighlight', new BufferAttribute(new Float32Array(buffers.vertexCount), 1));
    g.setIndex(new BufferAttribute(buffers.index, 1));
    this.geometry = g;
    this.residueIndex = buffers.residueIndex;
    this.main = meshOf(g, materials.ribbonLit, RENDER_ORDER.opaqueMesh);
    this.pick = meshOf(g, materials.pickPushed, RENDER_ORDER.opaqueMesh);
    this.shadow = meshOf(g, materials.ribbonShadow, RENDER_ORDER.opaqueMesh);
    const hullDir = new BufferAttribute(averagedNormals(buffers.position, buffers.normal), 3);
    const hullGeom = (): [BufferGeometry, BufferAttribute] => {
      const hg = new BufferGeometry();
      for (const name of ['position', 'normal', 'color', 'aPick']) hg.setAttribute(name, g.getAttribute(name));
      hg.setAttribute('aHullDir', hullDir);
      const a = new BufferAttribute(new Float32Array(buffers.vertexCount), 1);
      hg.setAttribute('aHighlight', a);
      hg.setIndex(g.getIndex());
      return [hg, a];
    };
    const [hg, ha] = hullGeom();
    const [sg, sa] = hullGeom();
    this.hullGeometry = { hover: hg, selection: sg };
    this.highlight = { hover: ha, selection: sa };
    this.hulls = {
      hover: meshOf(hg, materials.hull(INTERACTION.hoverColor), RENDER_ORDER.hull),
      selection: meshOf(sg, materials.hull(INTERACTION.selectionColor), RENDER_ORDER.hull),
    };
    this.hulls.hover.visible = false;
    this.hulls.selection.visible = false;
  }

  /** Mark residues for the inverse-hull pass of one layer; null clears it. */
  setHighlight(layer: HullLayer, residues: Set<number> | null): void {
    if (residues === null && this.current[layer] === null) return;
    const a = this.highlight[layer].array as Float32Array;
    let any = false;
    for (let i = 0; i < a.length; i++) { const on = residues !== null && residues.has(this.residueIndex[i]!); a[i] = on ? 1 : 0; any ||= on; }
    this.highlight[layer].needsUpdate = true;
    this.current[layer] = residues;
    this.hulls[layer].visible = any;
  }

  dispose(): void { this.geometry.dispose(); this.hullGeometry.hover.dispose(); this.hullGeometry.selection.dispose(); }
}
